import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { MoreVertical, Pencil, Trash2, Database, FileText } from 'lucide-react'
import type { KnowledgeBase } from '@/schemas/knowledge-base'

interface KBCardProps {
  kb: KnowledgeBase
  onClick?: () => void
  onEdit?: () => void
  onDelete?: () => void
}

// Status badge configuration
const STATUS_CONFIG = {
  active: {
    label: 'Ativa',
    bg: 'bg-emerald-500/20',
    text: 'text-emerald-400',
  },
  inactive: {
    label: 'Inativa',
    bg: 'bg-amber-500/20',
    text: 'text-amber-400',
  },
} as const

export function KBCard({ kb, onClick, onEdit, onDelete }: KBCardProps) {
  const status = kb.is_active ? 'active' : 'inactive'
  const statusConfig = STATUS_CONFIG[status]

  return (
    <Card
      variant="glass"
      className="p-6 cursor-pointer group hover:border-emerald-500/30 transition-all duration-200"
      onClick={onClick}
    >
      {/* Header: Avatar + Status + Menu */}
      <div className="flex items-start justify-between mb-4">
        {/* Avatar */}
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500/20 to-blue-500/20 flex items-center justify-center">
          <Database className="w-7 h-7 text-emerald-400" />
        </div>

        {/* Status + Menu */}
        <div className="flex items-center gap-2">
          {/* Status Badge */}
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig.bg} ${statusConfig.text}`}
          >
            {statusConfig.label}
          </span>

          {/* Menu (visible on hover) */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="glass border-gray-700/50">
              {onEdit && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation()
                    onEdit()
                  }}
                  className="focus:bg-white/5"
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  Editar
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation()
                    onDelete()
                  }}
                  className="text-red-400 focus:bg-white/5 focus:text-red-400"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Excluir
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Name & Description */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-white mb-1 truncate">
          {kb.name}
        </h3>
        <p className="text-sm text-gray-400 line-clamp-2 min-h-[2.5rem]">
          {kb.description || 'Sem descricao'}
        </p>
      </div>

      {/* Footer: Document count & Chunking info */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5" />
          <span>
            {kb.document_count} {kb.document_count === 1 ? 'documento' : 'documentos'}
          </span>
        </div>
        <div className="text-gray-600">
          Chunk: {kb.chunking_config?.chunk_size || 1000}
        </div>
      </div>
    </Card>
  )
}

// New KB Card (for creating new knowledge base)
interface NewKBCardProps {
  onClick?: () => void
}

export function NewKBCard({ onClick }: NewKBCardProps) {
  return (
    <Card
      variant="glass"
      className="p-6 cursor-pointer border-dashed border-2 hover:border-emerald-500/50 hover:bg-white/5 transition-all duration-200 flex flex-col items-center justify-center min-h-[200px] gap-3"
      onClick={onClick}
    >
      <div className="w-14 h-14 rounded-xl bg-gray-700/30 flex items-center justify-center">
        <Database className="w-7 h-7 text-gray-500" />
      </div>
      <span className="text-gray-400 font-medium">Nova Base de Conhecimento</span>
    </Card>
  )
}
